import React from 'react';

import { MailOutlined } from '@ant-design/icons';
import { Alert, Form, Input, message, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { ParticipationRowStatus, RetrieveParticipationSchema } from '@app/models/users';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { RAW_INTERPOLATION } from '@app/services/i18n';

import { useInviteParticipantMutation } from '../../../../hooks';

import { ValidationSchema } from './data/validation';
import { IFormValues } from './interfaces';

interface IProps {
  participation?: RetrieveParticipationSchema;
  onClose: () => void;
}

export const InviteModal: React.FC<IProps> = ({ participation, onClose }) => {
  const [form] = Form.useForm<IFormValues>();
  const [formError, setFormError] = React.useState<string>();
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);
  const getErrorMessage = useApiErrorMessage();
  const invite = useInviteParticipantMutation();
  const isRepeated = participation?.status === ParticipationRowStatus.AwaitingActivation;

  React.useEffect(() => {
    if (participation) {
      setFormError(undefined);
      form.setFields([{ name: 'email', value: participation.last_invitation?.email || '', errors: [] }]);
    }
  }, [participation?.uid]);

  const handleSubmit = (values: IFormValues) => {
    if (!participation) {
      return;
    }
    const email = values.email.trim();
    setFormError(undefined);
    invite.mutate(
      { uid: participation.uid, data: { email } },
      {
        onSuccess: () => {
          message.success(spaceT.t('coalition.inviteSuccess', { email, ...RAW_INTERPOLATION }));
          onClose();
        },
        onError: (err) => {
          const { pointerErrors } = getFormErrorsFromResponse(values, err, validationT.t);
          if (pointerErrors.length) {
            form.setFields(pointerErrors);
          } else {
            setFormError(getErrorMessage(err));
          }
        },
      }
    );
  };

  const yupSync = {
    async validator({ field }: any, value: any) {
      await ValidationSchema(validationT.t).validateSyncAt(field, { [field]: value });
    },
  };

  return (
    <Modal
      open={!!participation}
      title={isRepeated ? spaceT.t('coalition.inviteAgainTitle') : spaceT.t('coalition.inviteTitle')}
      okText={commonT.t('buttonInvite')}
      okButtonProps={{ icon: <MailOutlined /> }}
      cancelText={commonT.t('buttonCancel')}
      onOk={() => form.submit()}
      onCancel={onClose}
      confirmLoading={invite.isLoading}
      forceRender
    >
      {participation && (
        <Typography.Paragraph type="secondary">
          {spaceT.t('coalition.inviteDescription', {
            name: participation.legal_entity.name,
            module: commonT.t(`module.${participation.module}` as const),
            ...RAW_INTERPOLATION,
          })}
          {isRepeated && ` ${spaceT.t('coalition.inviteAgainHint')}`}
        </Typography.Paragraph>
      )}
      <Form<IFormValues> form={form} layout="vertical" onFinish={handleSubmit} disabled={invite.isLoading}>
        <Form.Item<IFormValues> name="email" required label={spaceT.t('coalition.email')} rules={[yupSync]}>
          <Input placeholder={spaceT.t('coalition.emailPlaceholder')} autoComplete="off" />
        </Form.Item>
      </Form>
      {formError && <Alert type="error" showIcon title={formError} />}
    </Modal>
  );
};
